// =============================================================================
// SEVRIC Recon — shared Firestore subscriptions.
//
// Every widget on Today (and most pages) needs to read the tenant's vehicles,
// vendors, and recon ledger. Centralizing the subscriptions here means:
//   - One Firestore listener per collection across the whole page, not one
//     per widget (avoids paying for the same data multiple times)
//   - Consistent shape — every consumer sees the same fields
//   - Easy to swap to a server-side aggregator later without touching pages
//
// All hooks return `{ data, loading, error, tenantId }`. `data` is always an
// array (empty until the snapshot lands; never null), so consumers don't have
// to null-check.
// =============================================================================
import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { DEMO, DEMO_VEHICLES, DEMO_VENDORS, DEMO_LEDGER } from "./demo.js";

// -----------------------------------------------------------------------------
// useTenantVehicles — every vehicle for the caller's tenant. Live.
// -----------------------------------------------------------------------------
export function useTenantVehicles() {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (DEMO) { setData(DEMO_VEHICLES); setLoading(false); return; }
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    const q = query(collection(db, "vehicles"), where("tenantId", "==", tenantId));
    const unsub = onSnapshot(q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setData(rows); setLoading(false); setError(null);
      },
      (err) => {
        console.warn("[useTenantVehicles] subscribe failed:", err?.message);
        setError(err); setLoading(false);
      }
    );
    return unsub;
  }, [tenantId]);

  return { data, loading, error, tenantId };
}

// -----------------------------------------------------------------------------
// useTenantVendors — every vendor for the caller's tenant. Live.
// -----------------------------------------------------------------------------
export function useTenantVendors() {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (DEMO) { setData(DEMO_VENDORS); setLoading(false); return; }
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    const q = query(collection(db, "vendors"), where("tenantId", "==", tenantId));
    const unsub = onSnapshot(q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setData(rows); setLoading(false); setError(null);
      },
      (err) => {
        console.warn("[useTenantVendors] subscribe failed:", err?.message);
        setError(err); setLoading(false);
      }
    );
    return unsub;
  }, [tenantId]);

  return { data, loading, error, tenantId };
}

// -----------------------------------------------------------------------------
// useTenantLedger — recon ledger entries (every meaningful event on the floor).
// Ordered newest first. `limitTo` defaults to 50 for performance.
// -----------------------------------------------------------------------------
export function useTenantLedger({ limitTo = 50 } = {}) {
  const { profile } = useAuth();
  const tenantId = profile?.tenantId || null;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (DEMO) { setData(DEMO_LEDGER.slice(0, limitTo)); setLoading(false); return; }
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    const q = query(collection(db, "reconLedger"), where("tenantId", "==", tenantId));
    const unsub = onSnapshot(q,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (toMs(b.at) || 0) - (toMs(a.at) || 0))
          .slice(0, limitTo);
        setData(rows); setLoading(false); setError(null);
      },
      (err) => {
        console.warn("[useTenantLedger] subscribe failed:", err?.message);
        setError(err); setLoading(false);
      }
    );
    return unsub;
  }, [tenantId, limitTo]);

  return { data, loading, error, tenantId };
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
export function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts === "number") return ts;
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
}

export function ageInDays(ts) {
  const ms = toMs(ts);
  if (!ms) return null;
  return Math.floor((Date.now() - ms) / (1000 * 60 * 60 * 24));
}

export function ageInHours(ts) {
  const ms = toMs(ts);
  if (!ms) return null;
  return Math.floor((Date.now() - ms) / (1000 * 60 * 60));
}

export function relativeWhen(ts) {
  const ms = toMs(ts);
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 7)   return `${d}d ago`;
  return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Returns the vehicle's stage id (handles legacy field name).
export function stageOfVehicle(v) {
  return v.currentStage || v.currentStep || "acquisition";
}
